import * as R from 'remeda';
import { DateTime, Duration } from 'luxon';

import { toDateTime } from '@repo/common/utils';
import { ObjectIdString } from '@repo/common/schemas';
import { ObjectId } from '@repo/common/interfaces';
import { FileDownloadDTO } from '@repo/common/schemas/models';

export { toDateTime, delay, exhaustiveSwitchCheck, noop } from '@repo/common/utils';

export function getCauseChain(error: Error | string | unknown) {
  return getCauses(error)
    .join(':')
    .split(':')
    .map((str) => str.trim())
    .filter((str) => str && str !== 'Error')
    .join(': ');

  function getCauses(error: Error | string | unknown) {
    const causeChain: string[] = [];

    while (error) {
      causeChain.push(getMessage(error));
      error = R.isError(error) ? error.cause : null;
    }

    return causeChain;
  }

  function getMessage(error: Error | string | unknown): string {
    if (R.isString(error)) return error;
    if (!error) return '';
    if (R.isError(error)) return error.toString();
    return `${error}`;
  }
}

export function getRandomAuthCode() {
  return `testAuthCode_xx${Math.floor(Date.now().valueOf() / 3)}_xx`;
}

// Generates an objectId string for frontend forms
export function createObjectIdString(): ObjectIdString {
  const _id = new ObjectId();
  return _id.toString();
}

// format timestamp YYYY-MM-DD HH:MM:SS
export function formatTimestamp(timestamp: string | DateTime) {
  return toDateTime(timestamp).toLocaleString(DateTime.DATETIME_SHORT);
}

export function toValidDateTime(val: unknown): DateTime<true> {
  const result = toDateTime(val, true);
  return result;
}

// Format minutes into human-readable duration using Luxon Duration
export function formatMinutes(minutes: number): string {
  return Duration.fromObject({ minutes }).rescale().toHuman({ unitDisplay: 'short' });
}

export async function saveFileExport(file: FileDownloadDTO) {
  if (typeof window === 'undefined') {
    return;
  }
  const { content, contentType, filename } = file;

  const bytes = Uint8Array.from(atob(content), (char) => char.charCodeAt(0));
  await saveFileBlob(new Blob([bytes], { type: contentType }), filename);
}

export async function saveFileBlob(blob: Blob, fileName: string): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }
  console.log(`Saving file ${fileName}`);
  // Create a download link
  const url_object = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url_object;
  link.download = fileName;

  // Trigger the download
  document.body.appendChild(link);
  link.click();

  // Clean up
  window.URL.revokeObjectURL(url_object);
  document.body.removeChild(link);
}

interface CopyElementOptions {
  // If true, only copy the inner HTML, e.g., blockquote content
  inner?: boolean;
}

export async function copyElement(element?: HTMLElement | null, options?: CopyElementOptions) {
  const text = element?.innerText;
  const html = options?.inner ? element?.innerHTML : element?.outerHTML;

  if (!text || !html) {
    console.log('Nothing to copy', { element });
    return false;
  }

  try {
    // try Clipboard API first (will fail in iframe contexts like Bullhorn)
    if (navigator.clipboard && window.isSecureContext) {
      const clipboardItem = new ClipboardItem({
        'text/plain': new Blob([text], { type: 'text/plain' }),
        'text/html': new Blob([html], { type: 'text/html' }),
      });
      await navigator.clipboard.write([clipboardItem]);
      console.log('Copied text to clipboard', { text });
      return true;
    }
  } catch (error) {
    console.log('Clipboard API failed, falling back to execCommand', error);
  }

  return copyHtml(html);
}

export async function copyHtml(html: string): Promise<boolean> {
  try {
    // Create a temporary contentEditable div
    const div = document.createElement('div');
    div.contentEditable = 'true';
    div.innerHTML = html;
    div.style.position = 'absolute';
    div.style.left = '-9999px';
    div.style.opacity = '0';
    div.style.pointerEvents = 'none';
    document.body.appendChild(div);

    // Select the content
    const range = document.createRange();
    range.selectNodeContents(div);
    const selection = window.getSelection();
    if (!selection) {
      document.body.removeChild(div);
      return false;
    }

    selection.removeAllRanges();
    selection.addRange(range);

    const success = document.execCommand('copy');

    // Clean up
    selection.removeAllRanges();
    document.body.removeChild(div);

    if (success) {
      console.log('Copied text to clipboard using execCommand fallback');
    } else {
      console.log('Failed to copy text using execCommand');
    }

    return success;
  } catch (error) {
    console.error('Error in copyHtml fallback:', error);
    return false;
  }
}
